import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Heart } from "lucide-react";
import { BUNDLES, ETSY_SHOP, slug } from "../data/site";
import { PRODUCTS } from "../data/catalog";
import { useEtsyListings, withEtsy } from "../data/etsy";
import { ProductCard } from "./ProductCard";
import { Reveal } from "./motion";

export default function BundlePage() {
  const { slug: bundleSlug } = useParams();
  const etsyListings = useEtsyListings();
  const bundle = BUNDLES.find((b) => b.id === bundleSlug);
  if (!bundle) return <Navigate to="/" replace />;

  const items = bundle.items.map((item) => {
    const catalogMatch = PRODUCTS.find((p) => p.name === item.name);
    const merged = catalogMatch
      ? { ...item, phrase: item.phrase || catalogMatch.tagline, personalization: item.personalization || catalogMatch.personalization }
      : item;
    return withEtsy(merged, etsyListings);
  });

  return (
    <main data-testid={`bundle-page-${bundle.id}`}>
      <div className={`${bundle.bg} border-b ${bundle.inverse ? "border-cream/15" : "border-navy/10"}`}>
        <div className="mx-auto max-w-[1600px] px-6 pt-36 pb-16 md:px-12 md:pt-44 md:pb-20">
          <Reveal>
            <Link
              to="/"
              data-testid="bundle-back-link"
              className={`group inline-flex items-center gap-3 text-xs font-medium uppercase tracking-[0.25em] transition-colors duration-300 ${
                bundle.inverse ? "text-cream/60 hover:text-cream" : "text-navy/60 hover:text-navy"
              }`}
            >
              <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
              All bundles
            </Link>
            <p className="mt-10 font-caveat text-3xl font-semibold text-mustard">
              make it a bundle
              <Heart className="mb-1 ml-1 inline-block h-4 w-4 fill-mustard text-mustard" />
            </p>
            <h1
              className={`mt-4 max-w-5xl font-playfair text-5xl leading-[0.92] tracking-tight md:text-7xl lg:text-8xl ${
                bundle.inverse ? "text-cream" : "text-navy"
              }`}
            >
              {bundle.title}
            </h1>
            <p
              className={`mt-8 max-w-xl font-playfair text-xl italic leading-relaxed md:text-2xl ${
                bundle.inverse ? "text-cream/70" : "text-navy/70"
              }`}
            >
              “{bundle.line}”
            </p>
            <p
              className={`mt-8 text-[11px] font-medium uppercase tracking-[0.3em] ${
                bundle.inverse ? "text-cream/50" : "text-navy/50"
              }`}
            >
              These pieces work together — each is its own Etsy listing, sold separately
            </p>
          </Reveal>
        </div>
      </div>

      <div className="mx-auto max-w-[1600px] px-6 py-20 md:px-12 md:py-24">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((item, i) => (
            <Reveal key={item.name} delay={Math.min((i % 4) * 0.06, 0.24)} className="h-full">
              <ProductCard item={item} setId={`bundle-${bundle.id}`} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <div className="flex flex-wrap items-center justify-between gap-6 border border-navy/25 bg-blush/50 px-8 py-7 md:px-10">
            <div>
              <p className="max-w-md text-sm leading-relaxed text-navy/70">
                Build this bundle your way — shop each type and pick the designs you love.
              </p>
              <div className="mt-4 flex flex-wrap gap-3">
                {bundle.parts.map((p) => (
                  <Link
                    key={p.label}
                    to={p.to}
                    data-testid={`bundle-page-link-${slug(p.label)}`}
                    className="rounded-full border border-navy/40 px-5 py-2 text-[10px] font-medium uppercase tracking-[0.25em] text-navy transition-transform duration-300 hover:scale-95"
                  >
                    {p.label}
                  </Link>
                ))}
              </div>
            </div>
            <a
              href={ETSY_SHOP}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="bundle-follow-etsy-btn"
              className="group flex items-center gap-3 rounded-full bg-navy px-7 py-3.5 text-xs font-medium uppercase tracking-[0.25em] text-cream transition-transform duration-300 hover:scale-95"
            >
              Follow the shop on Etsy
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </a>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
